import {
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { saveAs } from "file-saver";
import React, { useEffect, useMemo, useState } from "react";
import { FoundCountBadge } from "../../../components/common/FoundCountBadge";
import { useTechnologiesDictionary } from "../../../hooks/queries/useTechnologiesDictionary";
import { useTechnologyCanonicalsDictionary } from "../../../hooks/queries/useTechnologyCanonicalsDictionary";
import { usePositionProfilesWithTechnologies } from "../../position-profiles";
import { ReportActionsToolbar } from "../components/ReportActionsToolbar";
import { ReportCriteriaBuilder, ReportCriteriaChip } from "../components/ReportCriteriaBuilder";
import { ReportCvExportModal } from "../components/ReportCvExportModal";
import { ReportEmptyState } from "../components/ReportEmptyState";
import { TechnologyReportResultsTable } from "../components/TechnologyReportResultsTable";
import { useReportSelection } from "../hooks/useReportSelection";
import { useTechnologyReportSearch } from "../hooks/useTechnologyReportSearch";
import {
  TechnologyReportMatchMode,
  TechnologyReportMatchModeValue
} from "../models/technologyReport.model";
import {
  positionProfileResolveService,
  technologyGraphService,
  technologyReportService,
} from "../services/technologyReportService";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

interface PendingCanonical {
  id: number;
  name: string;
  technologyIds: number[];
}

interface PendingProfile {
  id: number;
  name: string;
  technologyIds: number[];
}

export const TechnologyReport: React.FC = () => {
  const { data: technologies = [] } = useTechnologiesDictionary();
  const { data: canonicals = [] } = useTechnologyCanonicalsDictionary();
  const { data: profiles } = usePositionProfilesWithTechnologies();

  const [chips, setChips] = useState<ReportCriteriaChip[]>([]);
  const [matchMode, setMatchMode] = useState<TechnologyReportMatchModeValue>(TechnologyReportMatchMode.Any);

  const [techPickerValue, setTechPickerValue] = useState<string | null>(null);
  const [canonicalPickerValue, setCanonicalPickerValue] = useState<string | null>(null);
  const [profilePickerValue, setProfilePickerValue] = useState<string | null>(null);

  const [pendingCanonical, setPendingCanonical] = useState<PendingCanonical | null>(null);
  const [isExpandingCanonical, setIsExpandingCanonical] = useState(false);
  const [pendingProfile, setPendingProfile] = useState<PendingProfile | null>(null);
  const [isResolvingProfile, setIsResolvingProfile] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isExporting, setIsExporting] = useState(false);
  const [exportCvMode, setExportCvMode] = useState<"selected" | "all" | null>(null);

  // Debounce filter changes so the query auto-fires 500 ms after the last chip
  // change or match-mode toggle — no Run Report button needed.
  const [debouncedChips] = useDebouncedValue(chips, 500);
  const [debouncedMatchMode] = useDebouncedValue(matchMode, 500);

  const selection = useReportSelection();

  // Reset to page 1 and clear row selection whenever the debounced filter changes.
  useEffect(() => {
    setPage(1);
    selection.handleClearAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedChips, debouncedMatchMode]);

  const searchRequest = useMemo(
    () =>
      debouncedChips.length > 0
        ? {
          technologyIds: debouncedChips.flatMap((c) =>
            c.children ? c.children.map((ch) => ch.id) : [c.id]
          ),
          matchMode: debouncedMatchMode,
          page,
          pageSize,
        }
        : null,
    [debouncedChips, debouncedMatchMode, page, pageSize]
  );

  const { data, isFetching } = useTechnologyReportSearch(searchRequest);

  const techOptions = useMemo(
    () => technologies.map((t) => ({ value: String(t.id), label: t.name })),
    [technologies]
  );
  const canonicalOptions = useMemo(
    () => canonicals.map((c) => ({ value: String(c.id), label: c.name })),
    [canonicals]
  );
  const profileOptions = useMemo(
    () =>
      (profiles ?? [])
        .filter((p) => p.isActive)
        .map((p) => ({ value: String(p.id), label: p.name })),
    [profiles]
  );

  const techNameById = useMemo(
    () => new Map(technologies.map((t) => [t.id, t.name])),
    [technologies]
  );

  const addChip = (chip: ReportCriteriaChip) => {
    setChips((prev) => (prev.some((c) => c.id === chip.id) ? prev : [...prev, chip]));
  };

  const handleAddTechnology = (value: string | null) => {
    if (!value) {
      return;
    }
    const tech = technologies.find((t) => t.id === Number(value));
    if (tech) {
      addChip({ id: tech.id, label: tech.name });
    }
    setTechPickerValue(null);
  };

  const handleSelectCanonical = async (value: string | null) => {
    if (!value) {
      return;
    }
    const canonical = canonicals.find((c) => c.id === Number(value));
    if (!canonical) {
      return;
    }
    setCanonicalPickerValue(null);
    setIsExpandingCanonical(true);
    try {
      const expansion = await technologyGraphService.expandCanonical(canonical.id);
      if (!expansion.resolved || expansion.technologyIds.length === 0) {
        return;
      }
      setPendingCanonical({
        id: canonical.id,
        name: canonical.name,
        technologyIds: expansion.technologyIds,
      });
    } finally {
      setIsExpandingCanonical(false);
    }
  };

  const handleConfirmCanonical = () => {
    if (pendingCanonical) {
      addChip({
        id: pendingCanonical.id,
        label: pendingCanonical.name,
        children: pendingCanonical.technologyIds.map((id) => ({
          id,
          label: techNameById.get(id) ?? `Technology #${id}`,
        })),
      });
    }
    setPendingCanonical(null);
  };

  const handleSelectProfile = async (value: string | null) => {
    if (!value) {
      return;
    }
    const profile = (profiles ?? []).find((p) => p.id === Number(value));
    if (!profile) {
      return;
    }
    setProfilePickerValue(null);
    setIsResolvingProfile(true);
    try {
      const resolved = await positionProfileResolveService.resolveProfileTechnologies(profile.id);
      setPendingProfile({
        id: profile.id,
        name: profile.name,
        technologyIds: resolved.technologyIds,
      });
    } finally {
      setIsResolvingProfile(false);
    }
  };

  const handleConfirmProfile = () => {
    if (!pendingProfile || pendingProfile.technologyIds.length === 0) {
      return;
    }
    addChip({
      id: pendingProfile.id,
      label: pendingProfile.name,
      children: pendingProfile.technologyIds.map((id) => ({
        id,
        label: techNameById.get(id) ?? `Technology #${id}`,
      })),
    });
    setPendingProfile(null);
  };

  const handleRemoveChip = (id: number) => {
    setChips((prev) => prev.filter((c) => c.id !== id));
  };

  const handleRemoveFromGroup = (chipId: number, childId: number) => {
    setChips((prev) =>
      prev
        .map((c) => {
          if (c.id !== chipId || !c.children) {
            return c;
          }
          const remaining = c.children.filter((ch) => ch.id !== childId);
          return remaining.length > 0 ? { ...c, children: remaining } : null;
        })
        .filter((c): c is ReportCriteriaChip => c !== null)
    );
  };

  const handleClearAllChips = () => {
    setChips([]);
  };

  const buildFilter = () => ({
    technologyIds: chips.flatMap((c) => c.children ? c.children.map((ch) => ch.id) : [c.id]),
    matchMode,
  });

  const handlePageSizeChange = (value: string | null) => {
    if (!value) {
      return;
    }
    setPageSize(Number(value));
    setPage(1);
  };

  const handleExportCvForSelected = () => setExportCvMode("selected");
  const handleExportCvForAll = () => setExportCvMode("all");

  const handleSelectedIdsChangedByReview = (ids: number[]) => {
    const idSet = new Set(ids);
    const toRemove = Array.from(selection.selectedIds).filter((id) => !idSet.has(id));
    const toAdd = ids.filter((id) => !selection.selectedIds.has(id));
    if (toRemove.length > 0) {
      selection.handleDeselectAllOnPage(toRemove);
    }
    if (toAdd.length > 0) {
      selection.handleSelectAllOnPage(toAdd);
    }
  };

  const downloadReportFile = async (format: "xlsx" | "csv") => {
    setIsExporting(true);
    try {
      const blob = await technologyReportService.exportFile(buildFilter(), format);
      const timestamp = new Date().toISOString().slice(0, 10);
      saveAs(blob, `TechnologyReport_${timestamp}.${format}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = () => downloadReportFile("xlsx");
  const handleExportCsv = () => downloadReportFile("csv");

  const rows = (data?.items ?? []).map((row) => ({
    personId: row.personId,
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    thumbnailBase64: row.thumbnailBase64,
    matchedTechnologyNames: row.matchedTechnologyNames,
  }));
  const pagePersonIds = rows.map((r) => r.personId);

  return (
    <Stack gap="xl" pb="xl">
      <Box>
        <Title order={2}>By Skills &amp; Technologies</Title>
        <Text c="dimmed" size="sm" mt={4}>
          Find people who have specific skills, or everyone matching a technology canonical or role profile.
        </Text>
      </Box>

      <Paper withBorder shadow="sm" radius="md" p="lg">
        <Stack gap="md">
          <Text fw={600} size="sm" tt="uppercase" c="dimmed">
            Add skills &amp; technologies
          </Text>

          <Select
            label="Technology"
            placeholder="Search technologies..."
            searchable
            clearable
            data={techOptions}
            value={techPickerValue}
            onChange={handleAddTechnology}
            w="100%"
          />

          <Select
            label="Technology Canonical"
            placeholder="Search canonical groups (e.g. React, .NET)..."
            searchable
            clearable
            data={canonicalOptions}
            value={canonicalPickerValue}
            onChange={handleSelectCanonical}
            disabled={isExpandingCanonical}
            rightSection={isExpandingCanonical ? <Loader size={14} /> : undefined}
            w="100%"
          />

          <Select
            label="Role / Position Profile"
            placeholder="Search profiles (e.g. Senior .NET Developer)..."
            searchable
            clearable
            data={profileOptions}
            value={profilePickerValue}
            onChange={handleSelectProfile}
            disabled={isResolvingProfile}
            rightSection={isResolvingProfile ? <Loader size={14} /> : undefined}
            w="100%"
          />

          {pendingCanonical && (
            <Alert
              color="brand"
              title={`${pendingCanonical.name} — expands to ${pendingCanonical.technologyIds.length} technology variant(s)`}
            >
              <ScrollArea.Autosize mah={150} mb="xs">
                <Stack gap={2}>
                  {pendingCanonical.technologyIds
                    .map((id) => techNameById.get(id))
                    .filter((n): n is string => n !== undefined)
                    .map((name) => (
                      <Text key={name} size="sm">{name}</Text>
                    ))}
                </Stack>
              </ScrollArea.Autosize>
              <Group gap="xs">
                <Button size="xs" color="brand" onClick={handleConfirmCanonical}>
                  Add all
                </Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => setPendingCanonical(null)}>
                  Cancel
                </Button>
              </Group>
            </Alert>
          )}

          {pendingProfile && (
            <Alert
              color="brand"
              title={`${pendingProfile.name} — ${pendingProfile.technologyIds.length} matching technology variant(s)`}
            >
              <ScrollArea.Autosize mah={150} mb="xs">
                <Stack gap={2}>
                  {pendingProfile.technologyIds
                    .map((id) => techNameById.get(id))
                    .filter((n): n is string => n !== undefined)
                    .map((name) => (
                      <Text key={name} size="sm">{name}</Text>
                    ))}
                </Stack>
              </ScrollArea.Autosize>
              <Group gap="xs">
                <Button size="xs" color="brand" onClick={handleConfirmProfile}>
                  Add profile
                </Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => setPendingProfile(null)}>
                  Cancel
                </Button>
              </Group>
            </Alert>
          )}

          {chips.length > 0 && (
            <>
              <Divider />
              <ReportCriteriaBuilder chips={chips} onRemove={handleRemoveChip} onRemoveFromGroup={handleRemoveFromGroup} onClearAll={handleClearAllChips} />
            </>
          )}

          <Divider />

          <Group align="center" gap="xl" wrap="wrap">
            <Group gap="xs" align="center">
              <Text size="sm" fw={500} c="dimmed">
                Match:
              </Text>
              <SegmentedControl
                size="xs"
                value={String(matchMode)}
                onChange={(v) => setMatchMode(Number(v) as TechnologyReportMatchModeValue)}
                data={[
                  { label: "Any of these", value: String(TechnologyReportMatchMode.Any) },
                  { label: "All of these", value: String(TechnologyReportMatchMode.All) },
                ]}
              />
            </Group>
          </Group>
        </Stack>
      </Paper>

      {chips.length === 0 ? (
        <ReportEmptyState />
      ) : (
        <Stack gap="md">
          {data && (
            <Group justify="space-between" align="center" wrap="wrap">
              <Group gap="md" align="center">
                <FoundCountBadge count={data.totalCount} singularNoun="person" pluralNoun="people" />
                <Group gap="xs" align="center">
                  <Text size="sm" c="dimmed">Per page:</Text>
                  <Select
                    size="xs"
                    w={70}
                    value={String(pageSize)}
                    onChange={handlePageSizeChange}
                    data={PAGE_SIZE_OPTIONS}
                    allowDeselect={false}
                  />
                </Group>
              </Group>
              <ReportActionsToolbar
                onExportCvForSelected={handleExportCvForSelected}
                onExportCvForAll={handleExportCvForAll}
                selectedCount={selection.selectedIds.size}
                totalCount={data.totalCount}
                exporting={isExporting}
              />
            </Group>
          )}
          <TechnologyReportResultsTable
            rows={rows}
            loading={!data || isFetching}
            selectedIds={selection.selectedIds}
            onToggleSelect={selection.handleToggleSelect}
            onSelectAllOnPage={() => selection.handleSelectAllOnPage(pagePersonIds)}
            onDeselectAllOnPage={() => selection.handleDeselectAllOnPage(pagePersonIds)}
            allSelectedOnPage={selection.allOnPageSelected(selection.selectedIds, pagePersonIds)}
            page={page}
            totalPages={data?.totalPages ?? 1}
            onPageChange={setPage}
          />
        </Stack>
      )}

      <ReportCvExportModal
        mode={exportCvMode}
        onClose={() => setExportCvMode(null)}
        selectedIds={selection.selectedIds}
        totalCount={data?.totalCount ?? 0}
        getMatchingPersonIds={() => technologyReportService.getMatchingPersonIds(buildFilter())}
        fileNamePrefix="CVs_TechnologyReport"
        onSelectedIdsChanged={handleSelectedIdsChangedByReview}
      />
    </Stack>
  );
};
